import { createApp } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import moshaToast from 'mosha-vue-toastify';
import VueCreditCardValidation from 'vue-credit-card-validation';
import Vue3Lottie from 'vue3-lottie';
import mixpanel from 'mixpanel-browser';
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import tracker from './tracker';
import 'mosha-vue-toastify/dist/style.css';

const app = createApp(App);
Sentry.init({
  app,
  dsn: process.env.VUE_APP_SENTRY_URL,
  release: `generic-web_${process.env.VUE_APP_ENV}@${require("../package.json").version}`,
  environment: process.env.VUE_APP_ENV,
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
    }),
  ],
  tracesSampleRate: 1.0,
});

app.use(moshaToast);
app.use(Vue3Lottie);
app.use(VueCreditCardValidation);
app.use(store);
app.use(VueAxios, axios);
app.use(router);

// mixpanel
mixpanel.init(process.env.VUE_APP_MIXPANEL, { track_pageview: "url-with-path-and-query-string" });
app.config.globalProperties.$mixpanel = mixpanel;
// tracker.methods.registerProperties(mixpanel);
app.config.globalProperties.$mixpanelEvent = tracker.analytics.events;
app.config.globalProperties.$mixpanelPayload = tracker.analytics.payLoad;

app.config.globalProperties.$trackUuid = tracker.methods.trackUuid;
app.config.globalProperties.$trackEvent = tracker.methods.trackEvent;
app.config.globalProperties.$trackUserProfile = tracker.methods.trackUserProfile;
app.config.globalProperties.$registerProperties = tracker.methods.registerProperties;

app.config.globalProperties.$filters = {
  formatMoney(amount) {
    if (!amount || Math.abs(amount) < 0.01) {
      return '₹0';
    }

    if (Intl) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    }
    const formattedAmount = amount % 1 === 0 ?
      amount.toFixed(0) :
      amount.toFixed(2);
    return `₹${formattedAmount.replace(/\d(?=(\d{3})+\.?)/g, "$&,")}`;
  },
  roundUTCDay(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + (d.getUTCHours() < 24 ? 0 : 1)));
  },
  formatDate(date, dateTime = false) {
    const d = new Date(date);
    const formattedDate = new Date(this.roundUTCDay(d).toISOString());
    if (!formattedDate) return "";
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const day = formattedDate.getDate().toString().padStart(2, "0");
    const monthIndex = formattedDate.getMonth();
    const year = formattedDate.getFullYear();
    const weekDay = formattedDate.getDay();
    const hour = formattedDate.getHours();
    const minute = formattedDate.getMinutes();
    if (dateTime) {
      return `${day} ${monthNames[monthIndex]} ${year}, ${dayNames[weekDay]} ${hour % 12
        }:${minute} ${hour < 12 ? "AM" : "PM"}`;
    }
    return `${day} ${monthNames[monthIndex]} ${year}`;
  },
  copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
    }, () => {
    });
  },
  panInputFormate(event, data) {
    const value = data || '';
    const key = event.key.toUpperCase();

    // Allow Backspace, Tab, Delete, Left/Right Arrow keys
    if (['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      return;
    }

    // Prevent input beyond 10 characters
    if (value.length >= 10) {
      event.preventDefault();
      return;
    }

    // First 5 characters: Only letters (A-Z)
    if (value.length < 5 && !/[A-Z]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Next 4 characters: Only numbers (0-9)
    if (value.length >= 5 && value.length < 9 && !/[0-9]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Last character: Only a letter (A-Z)
    if (value.length === 9 && !/[A-Z]/.test(key)) {
      event.preventDefault();
    }
  },
};

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      console.log("Service Worker registered with scope:", registration.scope);
    })
    .catch((error) => {
      console.error("Service Worker registration failed:", error);
    });
}

store.axios = app.axios;
store.$router = router;
app.mount("#app");

export default app;
